<template>
    <div
        class="ask-pendo-mention-popover"
        :data-mention-type="mentionData.type"
        @mouseenter="$emit('mouseenter')"
        @mouseleave="$emit('mouseleave')">
        <div
            class="ask-pendo-mention-popover__arrow"
            data-popper-arrow />
        <segment-hover-card
            v-if="mentionData.type === 'segment'"
            :segment-id="mentionData.id"
            :segment-dependents="segmentDependentsForMention" />
        <countable-hover-card
            v-if="isCountable"
            :id="mentionData.id"
            :type="mentionData.type" />
        <account-visitor-card-content
            v-if="isAccountOrVisitor"
            :id="mentionData.id"
            ref="accountVisitorCard"
            :kind="mentionData.type"
            :name="mentionData.text" />
        <app-hover-card
            v-if="isApp"
            :app-id="mentionData.id" />
        <product-area-hover-card
            v-if="isProductArea"
            :group-id="mentionData.id" />
    </div>
</template>
<script>
import { createPopper } from '@popperjs/core';
import SegmentHoverCard from '@/common/hover-card/segment-hover-card/segment-hover-card.vue';
import CountableHoverCard from '@/common/hover-card/countable-hover-card/countable-hover-card.vue';
import AccountVisitorCardContent from '@/common/account-visitor-hover-card/account-visitor-card-content/account-visitor-card-content.vue';
import AppHoverCard from '@/common/hover-card/app-hover-card/app-hover-card.vue';
import ProductAreaHoverCard from '@/common/hover-card/product-area-hover-card/product-area-hover-card.vue';

const POPPER_OPTIONS = {
    placement: 'top',
    strategy: 'fixed',
    modifiers: [
        {
            name: 'offset',
            options: {
                offset: [0, 8],
            },
        },
        {
            name: 'preventOverflow',
            options: {
                padding: { top: 60 },
            },
        },
        {
            name: 'flip',
            options: {
                padding: { top: 60 },
                fallbackPlacements: ['bottom', 'left', 'right'],
            },
        },
        {
            name: 'arrow',
            options: {
                element: null, // set in createPopper
            },
        },
    ],
};

export default {
    name: 'AskPendoMentionPopover',
    components: {
        AccountVisitorCardContent,
        AppHoverCard,
        CountableHoverCard,
        ProductAreaHoverCard,
        SegmentHoverCard,
    },
    props: {
        mentionData: {
            type: Object,
            default: () => ({ type: '', id: '', text: '' }),
        },
        referenceElement: {
            type: Object,
            default: null,
        },
        segmentDependents: {
            type: Object,
            default: () => ({}),
        },
    },
    emits: ['mouseenter', 'mouseleave'],
    data () {
        return {
            popperInstance: null,
        };
    },
    computed: {
        segmentDependentsForMention () {
            return this.segmentDependents[this.mentionData?.id];
        },
        isCountable () {
            return (
                this.mentionData.type === 'feature' ||
                this.mentionData.type === 'page' ||
                this.mentionData.type === 'trackEvent' ||
                this.mentionData.type === 'track_event'
            );
        },
        isAccountOrVisitor () {
            return this.mentionData.type === 'account' || this.mentionData.type === 'visitor';
        },
        isApp () {
            return this.mentionData.type === 'app';
        },
        isProductArea () {
            return this.mentionData.type === 'productArea';
        },
    },
    watch: {
        referenceElement: {
            handler (refEl) {
                if (refEl && this.$el) {
                    this.initPopper(refEl);
                } else {
                    this.destroyPopper();
                }
            },
            immediate: true,
        },
        mentionData: {
            handler () {
                if (this.isAccountOrVisitor) {
                    this.$nextTick(() => {
                        this.$refs.accountVisitorCard?.setFetchingMetadata(false);
                        this.$refs.accountVisitorCard?.showHandle();
                    });
                }
            },
            immediate: true,
        },
    },
    mounted () {
        if (this.referenceElement && this.$el) {
            this.initPopper(this.referenceElement);
        }
    },
    beforeUnmount () {
        this.destroyPopper();
    },
    methods: {
        initPopper (referenceElement) {
            if (!this.$el || !referenceElement) return;

            this.destroyPopper();

            const arrowEl = this.$el.querySelector('[data-popper-arrow]');
            const options = {
                ...POPPER_OPTIONS,
                modifiers: POPPER_OPTIONS.modifiers.map((mod) =>
                    (mod.name === 'arrow'
                        ? { ...mod, options: { ...mod.options, element: arrowEl } }
                        : mod),
                ),
            };

            this.popperInstance = createPopper(referenceElement, this.$el, options);
        },
        destroyPopper () {
            if (this.popperInstance) {
                this.popperInstance.destroy();
                this.popperInstance = null;
            }
        },
    },
};
</script>
<style scoped lang="less">
.ask-pendo-mention-popover {
    box-shadow: 0 4px 12px var(--semantic-drop-shadow-base);
    border: 1px solid var(--palette-gray-20);
    background: var(--palette-gray-0);
    pointer-events: auto;
    z-index: 99999;

    :deep(.account-visitor-card-content) {
        max-height: 400px;
        max-width: 320px;
        overflow-y: auto;
        padding: 16px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        .account-visitor-card-content__metrics-container {
            display: flex;
            gap: 8px;
            flex-wrap: nowrap;
        }

        .account-visitor-card-content__metrics {
            flex: 1;
            min-width: 0; // prevents overflow
        }

        .account-visitor-card-content__metric {
            padding: 6px 4px;
        }

        .account-visitor-card-content__metric-value {
            font-size: 13px;
        }

        .account-visitor-card-content__metric-label {
            font-size: 10px;
            text-align: center;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }

    &[data-mention-type^='segment'] {
        border-radius: 3px;
        border: 0.5px solid var(--palette-gray-40);
        background: var(--palette-gray-0);
        box-shadow: 0 4px 4px 0 var(--semantic-drop-shadow-base);
    }

    .ask-pendo-mention-popover__arrow,
    .ask-pendo-mention-popover__arrow::before {
        position: absolute;
        width: 8px;
        height: 8px;
        background: inherit;
    }

    .ask-pendo-mention-popover__arrow {
        visibility: hidden;
    }

    .ask-pendo-mention-popover__arrow::before {
        visibility: visible;
        content: '';
        transform: rotate(45deg);
        border: 1px solid var(--palette-gray-40);
    }

    // Top placement arrow
    &[data-popper-placement^='top'] > .ask-pendo-mention-popover__arrow {
        bottom: -4px;

        &::before {
            border-left: none;
            border-top: none;
        }
    }

    // Bottom placement arrow
    &[data-popper-placement^='bottom'] > .ask-pendo-mention-popover__arrow {
        top: -4px;

        &::before {
            border-right: none;
            border-bottom: none;
        }
    }

    // Left placement arrow
    &[data-popper-placement^='left'] > .ask-pendo-mention-popover__arrow {
        right: -4px;

        &::before {
            border-left: none;
            border-bottom: none;
        }
    }

    // Right placement arrow
    &[data-popper-placement^='right'] > .ask-pendo-mention-popover__arrow {
        left: -4px;

        &::before {
            border-right: none;
            border-top: none;
        }
    }
}
</style>
